(() => {
  if (customElements.get('product-card-gallery')) return;

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) target.initialize();
        });
      }, { rootMargin: '250px' })
    : null;

  class ProductCardGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('[data-gallery-track]');
      this.link = this.querySelector('[data-gallery-link]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.status = this.querySelector('[data-gallery-status]');
      if (!this.track || this.slides.length < 2) return;

      this.initialized = false;
      this.activeIndex = 0;
      this.pointerId = null;
      this.suppressClickUntil = 0;
      const activate = () => {
        this.initialize();
        this.loadImages();
      };
      ['pointerdown', 'pointerover', 'focusin', 'click'].forEach((type) => {
        this.addEventListener(type, activate, { capture: true, signal: this.controller.signal });
      });
      if (observer) observer.observe(this);
      else this.initialize();
    }

    initialize() {
      if (this.initialized || !this.isConnected || !this.track) return;
      this.initialized = true;
      observer?.unobserve(this);
      const signal = this.controller.signal;
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false, signal });
      this.track.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal });
      this.link?.addEventListener('click', (event) => {
        if (performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-gallery-dot], [data-gallery-prev], [data-gallery-next]');
        if (!button || button.closest('product-card-gallery') !== this || button.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        const index = button.hasAttribute('data-gallery-dot')
          ? Number(button.dataset.galleryIndex || 0)
          : this.activeIndex + (button.hasAttribute('data-gallery-next') ? 1 : -1);
        this.goTo(index);
      }, { signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.goTo(this.activeIndex, false));
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.goTo(this.activeIndex, false), { signal });
      }
      this.update(this.nearestIndex);
    }

    loadImages() {
      this.querySelectorAll('template[data-gallery-image]').forEach((template) => {
        template.replaceWith(template.content.cloneNode(true));
      });
    }

    disconnectedCallback() {
      observer?.unobserve(this);
      this.controller?.abort();
      this.controller = null;
      this.initialized = false;
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.scrollTimer) clearTimeout(this.scrollTimer);
    }

    get slideWidth() {
      return this.track?.clientWidth || 1;
    }

    get nearestIndex() {
      return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.slideWidth)));
    }

    onScroll() {
      this.loadImages();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.update(this.nearestIndex));
      clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.snapToNearest(), 110);
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.pointerId = event.pointerId;
      this.pointerType = event.pointerType;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startScrollLeft = this.track.scrollLeft;
      this.hasMoved = false;
      if (event.pointerType === 'mouse') {
        this.track.setPointerCapture?.(event.pointerId);
        this.track.classList.add('is-dragging');
      }
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      if (Math.abs(deltaX) > 7 && Math.abs(deltaX) > Math.abs(deltaY)) this.hasMoved = true;
      if (this.pointerType === 'mouse' && this.hasMoved) {
        event.preventDefault();
        this.track.scrollLeft = this.startScrollLeft - deltaX;
      }
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      if (this.hasMoved) this.suppressClickUntil = performance.now() + 350;
      if (this.pointerType === 'mouse') {
        this.track.releasePointerCapture?.(event.pointerId);
        this.track.classList.remove('is-dragging');
        this.snapToNearest();
      }
      this.pointerId = null;
      this.pointerType = null;
    }

    goTo(index, smooth = true) {
      const safeIndex = Math.max(0, Math.min(this.slides.length - 1, index));
      if (safeIndex > 0) this.loadImages();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: safeIndex * this.slideWidth, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
      this.update(safeIndex);
    }

    snapToNearest() {
      this.goTo(this.nearestIndex);
    }

    update(index) {
      this.activeIndex = index;
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });
      if (this.prevButton) this.prevButton.disabled = index <= 0;
      if (this.nextButton) this.nextButton.disabled = index >= this.slides.length - 1;
      if (this.status) this.status.textContent = this.slides[index].getAttribute('aria-label');
    }
  }

  customElements.define('product-card-gallery', ProductCardGallery);
})();
